import { ProductListItem } from '@/types';
import { create } from 'zustand';

// --- Типизация (для TypeScript) ---
export interface Untappd {
    score: string;
    url: string;
}

export interface ProductVariation {
    id: number;
    price: string;
    packaging_type: string;
    volume: number;
    stockQuantity: number;
    inStock: boolean;
    packQuantity: number;
    packHalfQuantity: number;
    name: string;
    vendor: string;
    label: string;
    style: string;
    untappd: Untappd;
    details: string;
}

export interface CartItem {
    id: number;
    quantity: number;
    price: string;
    amount: string;
    productVariation: ProductVariation;
}

interface CartState {
    items: CartItem[];
    count: number;
    amount: string;
    isLoading: boolean;
    error: string | null;

    // Действия (Actions)
    fetchCart: () => Promise<void>;
    setCartData: (data: {
        items: CartItem[];
        count: number;
        amount: string;
    }) => void;
    removeItem: (itemId: number) => Promise<void>;
    addItem: (variation: ProductListItem) => Promise<void>;
    updateQuantity: (
        id: number,
        quantity: number,
        handleBefore?: () => void,
        handleFinish?: () => void,
    ) => Promise<void>;
}

export const useCartFetchStore = create<CartState>((set, get) => ({
    // Начальное состояние
    items: [],
    count: 0,
    amount: '0 ₽',
    isLoading: true,
    error: null,

    setCartData: (data) => {
        set({
            items: data.items,
            count: data.count,
            amount: data.amount,
            error: null,
            isLoading: false,
        });
    },

    addItem: async (variation) => {
        set({ isLoading: true, error: null });

        const id = variation?.id;
        const quantity = variation?.packHalfQuantity;

        try {
            const response = await fetch(
                `/cart/${id}/add?quantity=${quantity}`,
                {
                    headers: {
                        // 'X-CSRF-TOKEN': csrfToken,
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                },
            );

            if (!response.ok) {
                throw new Error('Ошибка при добавлении товара в корзину');
            }

            const data = await response.json();

            window.Alpine.store('cart').update();

            // Обновляем состояние корзины через set()
            set({
                count: data.count,
                items: data.items,
                amount: data.amount,
                isLoading: false,
            });
        } catch (error) {
            set({
                error:
                    error instanceof Error
                        ? error.message
                        : 'Ошибка при добавлении',
                isLoading: false,
            });
        }
    },

    // Удаление товара из корзины
    removeItem: async (itemId: number) => {
        set({ isLoading: true, error: null });

        try {
            const csrfToken =
                document
                    .querySelector('meta[name="csrf-token"]')
                    ?.getAttribute('content') || '';

            const response = await fetch(`/cart/${itemId}/delete`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Ошибка при удалении товара');
            }

            const data = await response.json();
            window.Alpine.store('cart').update();

            set({
                count: data.count,
                items: data.items,
                amount: data.amount,
                isLoading: false,
            });
        } catch (error) {
            set({
                error:
                    error instanceof Error
                        ? error.message
                        : 'Ошибка при удалении',
                isLoading: false,
            });
        }
    },

    updateQuantity: async (
        id: number,
        quantity: number,
        handleBefore?: () => void,
        handleFinish?: () => void,
    ) => {
        set({ isLoading: true, error: null });
        handleBefore?.();

        try {
            const csrfToken =
                document
                    .querySelector('meta[name="csrf-token"]')
                    ?.getAttribute('content') || '';

            const response = await fetch(`/cart/${id}/quantity`, {
                method: 'PATCH',
                body: JSON.stringify({ quantity }),
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Ошибка при обновлении количества товара');
            }

            const data = await response.json();

            if (typeof window !== 'undefined' && window.Alpine) {
                window.Alpine.store('cart').update();
            }

            // Правильное обновление состояния в Zustand
            set({
                count: data.count,
                items: data.items,
                amount: data.amount,
                isLoading: false,
            });
        } catch (error) {
            set({
                error:
                    error instanceof Error
                        ? error.message
                        : 'Ошибка при обновлении',
                isLoading: false,
            });
        }
        handleFinish?.();
    },

    // Асинхронный экшен для получения данных с сервера
    fetchCart: async () => {
        set({ isLoading: true, error: null });

        try {
            const response = await fetch('/cart/fetch');

            if (!response.ok) {
                throw new Error('Ошибка при загрузке корзины');
            }

            const data = await response.json();

            // Обновляем состояние полученными данными
            set({
                items: data.items,
                count: data.count,
                amount: data.amount,
                isLoading: false,
            });
        } catch (error) {
            set({
                error:
                    error instanceof Error
                        ? error.message
                        : 'Неизвестная ошибка',
                isLoading: false,
            });
        }
    },
}));
